import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Background from "../components/Background";
import AyupathCard from "../components/AyupathCard";
import PrimaryButton from "../components/PrimaryButton";
import SecondaryButton from "../components/SecondaryButton";
import TeaserReveal from "../components/TeaserReveal";
import TimelineBar from "../components/TimelineBar";
import PastMatches, { loadMatches, matchSummary } from "../components/PastMatches";
import PreviewFeedback from "../components/PreviewFeedback";
import SampleReport from "../components/SampleReport";
import PreviewUnlocked from "../components/PreviewUnlocked";
import LockedChapters from "../components/LockedChapters";
import StickyOffer from "../components/StickyOffer";
import NotNowReasons from "../components/NotNowReasons";
import { rememberCheckout, rememberQuestion, rememberedQuestion } from "../components/IntakeForm";
import { useForm } from "../context/FormContext";
import useTeaserReveal from "../hooks/useTeaserReveal";
import { track } from "../utils/analytics";
import { buildCheckoutUrl } from "../services/checkout";

// Pages that sell the full report. The single-mums page is retired, but its
// earlier previews can still be bought from.
const OFFER_VARIANTS = new Set(["vedic-timeline-v1", "single-mums-next-chapter-v1"]);

// Reports are written by hand, so the wait is stated before payment, not after.
const FULL_REPORT_SLA = process.env.REACT_APP_FULL_REPORT_SLA || "1 to 2 business days";
const FULL_REPORT_PRICE = "AUD $49";

// Worded around what the preview left out, so the report reads as more
// rather than a longer copy of what the reader has just seen.
const REPORT_AREAS = [
  "Every sub-period over the next 20 years, with its dates, not just the major periods above",
  "Career, relationships, money and home: what your chart shows for each, in plain English",
  "Which of your coming periods your chart links with each of those areas",
  "All 12 areas of your chart read in detail",
];

function monthYear(iso) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-AU", {
    month: "long", year: "numeric", timeZone: "UTC",
  });
}

/** The major period after the current one, if the timeline has it. */
export function nextTurningPoint(majorPeriods) {
  const periods = majorPeriods?.periods || [];
  const now = majorPeriods?.now || "";
  const current = periods.findIndex((p) => p.start <= now && now < p.end);
  return current >= 0 ? periods[current + 1] || null : null;
}

export default function Success() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const { formData, reset } = useForm();

  // Query param keeps the preview reachable after a refresh or shared link.
  const requestId = state?.requestId || searchParams.get("requestId") || "";
  const firstName = (formData.name || "").trim().split(" ")[0];
  const {
    phase, teaser, elapsedMs, landingVariant, emailStatus, birthTimeConfidence, majorPeriods, focusLinks, offerWindow,
  } = useTeaserReveal(requestId);
  const isNextChapter = OFFER_VARIANTS.has(landingVariant || formData.landingVariant);
  const checkoutUrl = isNextChapter
    ? buildCheckoutUrl(process.env.REACT_APP_NEXT_CHAPTER_CHECKOUT_URL || "", requestId)
    : "";

  const [matches] = useState(() => loadMatches(requestId));
  const summary = matchSummary(matches);
  const turningPoint = nextTurningPoint(majorPeriods);

  useEffect(() => {
    if (phase !== "ready") return;
    track("teaser_view", { requestId });
    if (summary) {
      track("past_matches_view", { requestId, match_score: `${summary.matched}-of-${summary.asked}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, requestId]);

  // The email's buy button lands here with ?buy=1: go straight to the offer.
  const offerRef = useRef(null);
  const readingRef = useRef(null);
  const fromEmailOffer = searchParams.get("buy") === "1";
  useEffect(() => {
    if (phase === "ready" && fromEmailOffer) {
      offerRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }
  }, [phase, fromEmailOffer]);

  function goHome() {
    reset();
    navigate("/", { replace: true });
  }

  // The locked chapters and the sticky bar lead to the offer, not to checkout.
  function showOffer(source) {
    track("offer_scroll_click", { requestId, source });
    offerRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }

  function unlockFullReport() {
    track("teaser_cta_click", { requestId });
    if (checkoutUrl) {
      track("checkout_started", { requestId, offer_id: "next-chapter-life-report" });
      rememberCheckout(requestId); // so the thank-you page can ask for their question
      window.location.assign(checkoutUrl);
    }
  }

  return (
    <Background>
      <div className="min-h-screen px-4 py-10 flex flex-col items-center">
        <div className="w-full max-w-md mx-auto flex-1 space-y-6">
          <AyupathCard>
            <div className="space-y-6">
              <div className="text-center space-y-3">
                {phase === "ready" ? <PreviewUnlocked majorPeriods={majorPeriods} /> : null}
                {phase === "ready" ? (
                  <div className="space-y-2">
                    <h2 className="text-2xl font-semibold text-gray-900">
                      {firstName ? `${firstName}, here's your preview` : "Here's your preview"}
                    </h2>
                    <p className="text-gray-600">
                      Your chart and where you are in your timeline.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <h2 className="text-2xl font-semibold text-gray-900">Your preview is on its way</h2>
                    <p className="text-gray-600">
                      {phase === "generating"
                        ? "Reading your chart now."
                        : "Your preview is being prepared."}
                    </p>
                  </div>
                )}
              </div>

              {phase === "generating" ? <GeneratingState elapsedMs={elapsedMs} /> : null}

              {phase === "ready" ? (
                <div className="space-y-5">
                  {majorPeriods?.periods?.length ? (
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
                      <TimelineBar periods={majorPeriods.periods} now={majorPeriods.now} />
                      {checkoutUrl ? (
                        <LockedChapters majorPeriods={majorPeriods} onClick={() => showOffer("locked_chapters")} />
                      ) : null}
                    </div>
                  ) : null}
                  {/* Proof before the reading, so the offer can follow the reading directly. */}
                  <PastMatches matches={matches} />
                  <div ref={readingRef} className="bg-orange-50 rounded-2xl p-5">
                    <TeaserReveal html={teaser} />
                  </div>

                  <div className="space-y-3">
                    {/* The preview says it is for reflection; the page no longer says it twice. */}
                    {checkoutUrl && offerWindow ? (
                      <OfferBridge offer={offerWindow} onClick={() => showOffer("bridge")} />
                    ) : null}
                    {birthTimeConfidence && birthTimeConfidence !== "exact" ? (
                      <div className="rounded-2xl border border-gray-200 p-5 space-y-3">
                        <h3 className="font-semibold text-gray-900">There's more we could tell you</h3>
                        <p className="text-sm text-gray-700">
                          You told us your birth time is approximate, so we left out the parts
                          that depend on it — the years things shifted for you, and the periods
                          behind them. We'd rather leave them out than guess.
                        </p>
                        <p className="text-sm text-gray-700">
                          <strong className="text-gray-900">One minute out moves your timeline by about five days.</strong>{" "}
                          Fifteen minutes moves it by two and a half months.
                        </p>
                        <p className="text-sm text-gray-700">
                          A family horoscope written at your birth (a kendara, jathakam or kundli)
                          records the exact time, and so does a birth certificate or hospital card.
                        </p>
                        <SecondaryButton
                          onClick={() => navigate(`/update-birth-time?requestId=${encodeURIComponent(requestId)}`)}
                        >
                          I can find my exact time
                        </SecondaryButton>
                      </div>
                    ) : null}
                    {checkoutUrl ? (
                      <div ref={offerRef} className="rounded-2xl border border-gray-200 p-5 space-y-3">
                        {offerWindow ? (
                          // The reader's own question and the start of the full report's
                          // first-ranked window; the second window and the reasons stay for the report.
                          <>
                            <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Your complete report</p>
                            <h3 className="text-xl font-semibold text-gray-900 leading-snug">{offerWindow.question}</h3>
                            <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 space-y-2">
                              <p className="text-sm text-gray-800">
                                Your chart ranks a first window for {offerWindow.noun}{" "}
                                {offerWindow.start <= new Date().toISOString().slice(0, 10) ? "open now, since" : "starting"}
                              </p>
                              <p className="text-2xl font-semibold text-gray-900">{monthYear(offerWindow.start)}</p>
                              {offerWindow.more ? <p className="text-sm text-gray-800">and a second one after it.</p> : null}
                            </div>
                            <p className="text-sm text-gray-700">
                              Your full report explains{" "}
                              <strong className="text-gray-900">
                                {offerWindow.more ? "why these two were chosen" : "why this one was chosen"}
                              </strong>
                              , what {offerWindow.more ? "each" : "it"} is linked with in your chart, and what to do
                              with {offerWindow.more ? "them" : "it"}, plus your life chapters and the twenty years ahead.
                            </p>
                            {/* What readers asked of the offer in testing: what exactly the report tells them. */}
                            <div className="space-y-1.5">
                              <p className="text-sm font-semibold text-gray-900">Your full report answers:</p>
                              <ul className="text-sm text-gray-700 space-y-1">
                                {[
                                  `What kind of change in ${offerWindow.noun} ${monthYear(offerWindow.start)} is linked with, and why your chart marks it`,
                                  "What you could do in the months before it, starting now",
                                  ...(offerWindow.more ? ["When your second window comes, and how it differs from the first"] : []),
                                ].map((item) => (
                                  <li key={item} className="flex gap-2">
                                    <span className="text-orange-500" aria-hidden="true">✓</span>
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </>
                        ) : (
                        <>
                        <div className="space-y-1">
                          <h3 className="font-semibold text-gray-900">Your complete report</h3>
                          {focusLinks?.count ? (
                            // The reader's own question, left open: how many, not which.
                            <p className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5 text-sm text-gray-800">
                              Your chart links{" "}
                              <strong className="text-gray-900">
                                {focusLinks.count === 1 ? "one" : focusLinks.count} of your coming{" "}
                                {focusLinks.count === 1 ? "period" : "periods"}
                              </strong>{" "}
                              with {focusLinks.area}. Your full report shows{" "}
                              {focusLinks.count === 1 ? "which one, and what it asks" : "which ones, and what each asks"} of you.
                            </p>
                          ) : null}
                          {turningPoint ? (
                            <p className="text-sm text-gray-700">
                              Your next turning point is your{" "}
                              <strong className="text-gray-900">
                                {turningPoint.lord} major period, from {monthYear(turningPoint.start)}
                              </strong>
                              . Your full report shows what your chart links it with, and every
                              sub-period between now and then.
                            </p>
                          ) : null}
                          <p className="text-sm text-gray-600">
                            Built from the same chart, it covers:
                          </p>
                        </div>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {REPORT_AREAS.map((area) => (
                            <li key={area} className="flex gap-2">
                              <span className="text-orange-500" aria-hidden="true">•</span>
                              {area}
                            </li>
                          ))}
                        </ul>
                        </>
                        )}
                        <SampleReport requestId={requestId} />
                        <OwnQuestion requestId={requestId} />
                        <p className="text-sm text-gray-700">
                          <strong className="text-gray-900">Prepared personally for your chart</strong>{" "}
                          and emailed to you within {FULL_REPORT_SLA}.
                        </p>
                        <PrimaryButton onClick={unlockFullReport}>
                          Get my full report — {FULL_REPORT_PRICE}
                        </PrimaryButton>
                        <p className="text-xs text-gray-600 text-center">
                          <strong className="text-gray-900">14-day full refund</strong> if it isn't
                          useful to you. Just reply to the email.
                          One payment, no subscription, nothing recurring.
                        </p>
                        <NotNowReasons requestId={requestId} />
                      </div>
                    ) : isNextChapter ? (
                      <p className="text-sm text-gray-600 text-center">The complete report is not available to purchase yet.</p>
                    ) : null}
                    {/* After the offer, so the open text box never pushes it down. */}
                    <PreviewFeedback requestId={requestId} />
                    <p className="text-xs text-gray-500 text-center">
                      {emailStatus === "sent"
                        ? "A copy of your preview has been sent by email."
                        : emailStatus === "failed"
                          ? "We couldn't send your email copy. Your preview is available here."
                          : "Your email copy is being prepared. You can read your preview here."}
                    </p>
                  </div>
                </div>
              ) : null}

              {phase === "slow" || phase === "failed" || phase === "idle" ? (
                <FallbackState phase={phase} />
              ) : null}

              {/* Hidden while the preview is being written: leaving then loses it. */}
              {phase !== "generating" ? (
                <div className="text-center">
                  <SecondaryButton onClick={goHome}>Back to AyuPath</SecondaryButton>
                </div>
              ) : null}
            </div>
          </AyupathCard>
        </div>
      </div>
      {phase === "ready" && checkoutUrl ? (
        <StickyOffer readingRef={readingRef} offerRef={offerRef} price={FULL_REPORT_PRICE}
          onClick={() => showOffer("sticky_bar")} />
      ) : null}
    </Background>
  );
}

/** Under the reading: the reader's own question and its answer, then the way to the offer. */
function OfferBridge({ offer, onClick }) {
  const open = offer.start <= new Date().toISOString().slice(0, 10);
  return (
    <button type="button" onClick={onClick}
      className="group block w-full rounded-2xl border-2 border-orange-300 bg-white p-4 text-left hover:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500">
      <p className="text-sm text-gray-700">
        You asked: <strong className="text-gray-900">{offer.question}</strong>
      </p>
      <p className="mt-1 text-sm text-gray-700">
        Your chart's first window: <strong className="text-lg text-gray-900">{open ? "open now" : monthYear(offer.start)}</strong>
      </p>
      <p className="mt-2 text-sm font-semibold text-orange-700 group-hover:underline">
        Why this month, and what to do before it, is in your full report ↓
      </p>
    </button>
  );
}

/** Optional: the reader writes the question before paying; the thank-you page fills it in. */
function OwnQuestion({ requestId }) {
  const [text, setText] = useState(() => rememberedQuestion(requestId));
  return (
    <div className="rounded-xl bg-orange-50 px-3 py-3 space-y-2">
      <label htmlFor="own-question" className="block text-sm text-gray-800">
        <strong className="text-gray-900">Ask your own question.</strong> What do you most want your
        report to answer? Your report answers it through your chart.
      </label>
      <textarea id="own-question" rows={2} maxLength={1000} value={text}
        placeholder="Optional. You can also tell us after you order."
        onChange={(e) => { setText(e.target.value); rememberQuestion(requestId, e.target.value); }}
        onBlur={() => { if (text.trim()) track("own_question_typed", { requestId }); }}
        className="w-full rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500" />
    </div>
  );
}

// What is really happening, in order, with when each step usually starts.
// Writing (and its quality check) is the long part, so it gets most of the bar.
export const GENERATING_STEPS = [
  { at: 0, label: "Calculating your birth chart" },
  { at: 6, label: "Working out your planetary periods" },
  { at: 14, label: "Writing your personal reading" },
  { at: 45, label: "Checking it carefully" },
];

/**
 * Progress from elapsed time: quick at first, then easing towards 95% so
 * the bar keeps moving through a long wait but never claims to be done.
 * A preview usually takes 30 to 90 seconds.
 */
export function generatingProgress(seconds) {
  return Math.min(95, Math.round(95 * (1 - Math.exp(-seconds / 35))));
}

function GeneratingState({ elapsedMs }) {
  const seconds = Math.floor(elapsedMs / 1000);
  const progress = generatingProgress(seconds);
  const current = GENERATING_STEPS.reduce((step, s, i) => (seconds >= s.at ? i : step), 0);

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-orange-100" role="progressbar"
          aria-label="Preparing your preview" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
          <div className="h-full rounded-full bg-gradient-to-r from-orange-400 to-orange-600 transition-[width] duration-1000 ease-out"
            style={{ width: `${Math.max(progress, 4)}%` }} />
        </div>
        <p className="text-center text-sm font-medium text-gray-800" aria-live="polite">
          {GENERATING_STEPS[current].label}…
        </p>
      </div>

      <ol className="space-y-2 rounded-2xl bg-orange-50 p-5">
        {GENERATING_STEPS.map((step, i) => (
          <li key={step.label} className="flex items-center gap-3 text-sm">
            <span aria-hidden="true"
              className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
                i < current ? "bg-orange-500 text-white"
                  : i === current ? "border-2 border-orange-500 bg-white motion-safe:animate-pulse"
                    : "border border-gray-300 bg-white"
              }`}>
              {i < current ? "✓" : ""}
            </span>
            <span className={i <= current ? "text-gray-900" : "text-gray-400"}>
              {step.label}
              <span className="sr-only">{i < current ? ": done" : i === current ? ": in progress" : ""}</span>
            </span>
          </li>
        ))}
      </ol>

      <p className="text-xs text-gray-500 text-center">
        This usually takes about a minute. Please keep this page open, and your preview will appear here.
      </p>
    </div>
  );
}

function FallbackState({ phase }) {
  return (
    <div className="bg-orange-50 rounded-2xl p-5 text-left space-y-3">
      <h3 className="text-sm font-semibold text-gray-900 text-center">
        {phase === "failed" ? "Taking longer than usual" : "We'll email it to you"}
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed">
        {phase === "idle"
          ? "Your reading is being prepared and will arrive by email shortly."
          : "Your reading is still being crafted. We'll send it to your inbox as soon as it's ready."}
      </p>
      <div className="flex items-start gap-2">
        <span className="text-orange-600 font-semibold text-sm mt-0.5">💡</span>
        <p className="text-sm text-gray-700">
          <span className="font-medium">Tip:</span> Check your Promotions/Spam folder.
        </p>
      </div>
    </div>
  );
}
